import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queries";

// Exponential-backoff bounds. A sleeping laptop or network bounce can fire
// onerror dozens of times in a row; flat 5s reconnects produce a storm in
// the browser console. Floor 1s → ceiling 30s, doubled per failure, reset
// to floor on a successful "connected" event.
const RECONNECT_FLOOR_MS = 1_000;
const RECONNECT_CEILING_MS = 30_000;

/**
 * Event → query-key invalidation map.
 *
 * Each server-broadcast event type lists the query keys that should be
 * invalidated when it arrives. Prefer narrow keys (e.g. only the affected
 * list) over the broad `queryKeys.app` snapshot so that a single event
 * does not trigger a full /api/app refetch.
 *
 * Predicates (hierarchical invalidation) use a `prefix` marker so any
 * query key starting with those segments is invalidated — used for
 * parameterised keys like outcomeSummary(days).
 */
interface PrefixKey {
  prefix: QueryKey;
}

type InvalidationTarget = QueryKey | PrefixKey;

const prefix = (key: QueryKey): PrefixKey => ({ prefix: key });

const EVENT_TARGETS: Record<string, InvalidationTarget[]> = {
  // Messaging — inbox/conversation lists live outside /api/app
  "message.sent": [queryKeys.app],
  "message.received": [queryKeys.app],
  "conversation.status_changed": [queryKeys.app],

  // Revenue attribution ledger
  "invoice.tracked": [
    queryKeys.paymentReminders,
    queryKeys.agingReport,
    queryKeys.revenueProof,
    queryKeys.app,
  ],
  "payment.confirmed": [
    queryKeys.paymentReminders,
    queryKeys.agingReport,
    queryKeys.revenueProof,
    queryKeys.app,
  ],

  // Customer portal actions (estimate approval / schedule request)
  "estimate.approved": [queryKeys.app, queryKeys.workQueue],
  "schedule.requested": [
    queryKeys.app,
    queryKeys.appointmentReminders,
    queryKeys.workQueue,
  ],

  // Demo mode — drives live-demo UI without user action
  "demo.inbound_reply": [queryKeys.app, queryKeys.workQueue],
  "demo.organic_inbound": [queryKeys.app, queryKeys.workQueue],

  // Future events (not yet emitted server-side, safe to list for preparation):
  // Analytics events fan out to every parameterised summary key.
  "outcome.tracked": [
    prefix(["outcomes", "summary"]),
    prefix(["analytics", "touch-effectiveness"]),
    queryKeys.app,
  ],
  "touch.delivered": [prefix(["analytics", "touch-effectiveness"])],
};

function invalidate(qc: QueryClient, target: InvalidationTarget): void {
  if ("prefix" in target) {
    void qc.invalidateQueries({ queryKey: target.prefix });
  } else {
    void qc.invalidateQueries({ queryKey: target });
  }
}

export function connectSse(queryClient: QueryClient): () => void {
  let es: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  let backoffMs = RECONNECT_FLOOR_MS;

  function open() {
    if (disposed) return;

    es = new EventSource("/api/events");

    es.addEventListener("connected", () => {
      // Connection succeeded — reset backoff so the next disconnect is
      // retried promptly rather than at the previous failure cadence.
      backoffMs = RECONNECT_FLOOR_MS;
    });

    for (const [eventType, targets] of Object.entries(EVENT_TARGETS)) {
      es.addEventListener(eventType, () => {
        for (const target of targets) {
          invalidate(queryClient, target);
        }
      });
    }

    es.onerror = () => {
      es?.close();
      es = null;
      if (!disposed) {
        reconnectTimer = setTimeout(open, backoffMs);
        backoffMs = Math.min(backoffMs * 2, RECONNECT_CEILING_MS);
      }
    };
  }

  open();

  return () => {
    disposed = true;
    es?.close();
    es = null;
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };
}
